import React, { useMemo, useContext, useCallback } from 'react';
import privileges from '@ubeya/shared-web/privileges';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import styled, { css } from 'styled-components';
import { Trans } from 'react-i18next';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getDaysOfWeek } from '@ubeya/shared/utils/time';
import { FlexColumn, Flex, FlexCenter, Loader, card } from '@ubeya/shared-web/components';
import { maxMedia } from '@ubeya/shared-web/style';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import useTags from '@ubeya/shared/hooks/account/useTags';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { selectedBranch } from '@ubeya/shared/atoms/shared';
import { SORT_BY_TIME } from '@ubeya/shared/utils/sorting';
import useSuppliers from '@ubeya/shared/hooks/account/useSuppliers';
import {
  selectedCopyEvent,
  selectedEventModal,
  newOrder,
  quickProject,
  selectedShiftModal,
  selectedBookingModal,
  selectedPublishProject,
  showAllBranchesMode,
  schedulingHideWeeklyPlanners,
  schedulingShowOnlyMainEvents,
  selectedMainProjectView,
  projectFilters
} from 'shared/atoms/scheduling';
import { filterMainProjectsByDateForMainEventSelection } from 'shared/utils/scheduling';
import { SchedulingContextSelector, SchedulingContextDispatcher } from 'scheduling/contexts/SchedulingContext';
import WeeklyPlannerBar from 'scheduling/components/WeeklyPlannerBar';
import useGroupedHolidays from 'scheduling/hooks/useGroupedHolidays';
import MainProjectBar from 'scheduling/components/MainProjectBar';
import { getMainProjectBarTrackCount } from 'scheduling/mainProjectBarLayout';
import { isOrderCellEntryPoint, shouldDisableCalendarAddEntryPoint } from 'scheduling/utils';

const Wrapper = styled(FlexColumn)`
  ${card}
  flex: 1;
  overflow: hidden;
  opacity: ${({ isLoading }) => (isLoading ? 0.3 : 1)};
`;

const Container = styled(FlexColumn)`
  flex: 1;
  overflow: auto;
`;

const Content = styled(FlexColumn)`
  flex: 1;
  min-height: min-content;
  ${maxMedia.mobile`min-width:1200px;`};
`;

const LoaderWrapper = styled(FlexCenter)`
  z-index: 1;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const Row = styled(Flex)`
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  ${({ isHeader }) =>
    isHeader ? `position: sticky; top: 0; z-index:4; background: ${({ theme }) => theme.colors.white};` : 'flex: 1;'};
`;

const HeaderCell = styled.div`
  padding: 10px 0;
  text-align: center;
`;

const Cell = styled(FlexColumn).attrs(() => ({ className: 'cell' }))`
  flex: 1 0 0%;
  min-width: 0px;
  padding: ${({ $dataCell }) => ($dataCell ? '0px' : '8px')};
  ${({ isHeader }) => !isHeader && 'min-height: 130px'};
  &:not(:first-child) {
    border-left: 1px solid ${({ theme }) => theme.colors.grey2};
  }
  background-color: ${({ theme }) => theme.colors.white};

  ${({ isToday }) =>
    isToday &&
    css`
      background-color: ${({ theme }) => theme.colors.grey1};
    `}

  cursor: ${({ onClick }) => onClick && 'pointer'};
`;

const BarRow = styled.div`
  position: absolute;
  top: ${({ $topOffset }) => $topOffset || 51}px;
  left: 0;
  right: 0;
  z-index: 2;
`;

const WeekRowWrapper = styled(FlexColumn)`
  position: relative;
  flex: 1;
  width: 100%;
`;

const HOLIDAY_CHIP_HEIGHT = 20;
const PLANNER_BAR_HEIGHT = 28;
const MAIN_PROJECT_BAR_HEIGHT = 28;
const CHIPS_GAP = 4;

const WeekLightView = ({ isLoading, startDate: start, DayRenderer }) => {
  const selectedBranchId = useRecoilValue(selectedBranch);

  const { mappedLabels, labels } = useLabels();
  const { mappedBranches } = useBranches();
  const { mappedLocations } = useLocations();
  const { mappedTags } = useTags();
  const { mappedClientBranches } = useCRM();
  const { mappedSuppliers } = useSuppliers();
  const { account, config } = useAccount();
  const projectFiltersForAccount = useRecoilValue(projectFilters)?.[account.id];
  const { push } = useHistory();
  const { hasEngagedSuppliers } = useSuppliers();
  const setSelectedEvent = useSetRecoilState(selectedEventModal);
  const setShowNewOrder = useSetRecoilState(newOrder);
  const myPrivileges = useContext(PrivilegesContext);
  const canCreateOrder = !!myPrivileges[privileges.CREATE_ORDER];
  const setSelectedShift = useSetRecoilState(selectedShiftModal);
  const setSelectedBooking = useSetRecoilState(selectedBookingModal);
  const setPublishProject = useSetRecoilState(selectedPublishProject);
  const setCopyEvent = useSetRecoilState(selectedCopyEvent);
  const setAddQuickProject = useSetRecoilState(quickProject);

  const showAllBranches = useRecoilValue(showAllBranchesMode);
  const hideWeeklyPlanners = useRecoilValue(schedulingHideWeeklyPlanners);
  const showOnlyMainEvents = useRecoilValue(schedulingShowOnlyMainEvents);
  const setSelectedMainProjectView = useSetRecoilState(selectedMainProjectView);
  const allowQuickJob = mappedBranches[selectedBranchId]?.scheduleConfig?.allowQuickJob;

  const isOrderOnlyCell = isOrderCellEntryPoint({
    isHost: account?.isHost,
    hasEngagedSuppliers,
    billingStatus: config?.billingStatus
  });
  const disableAddEntryPoint = shouldDisableCalendarAddEntryPoint({
    isHost: account?.isHost,
    hasEngagedSuppliers,
    billingStatus: config?.billingStatus,
    canCreateOrder
  });

  const preventCellClick = allowQuickJob || showAllBranches || disableAddEntryPoint;
  const { workDays: selectedWorkDays } = mappedBranches[selectedBranchId]?.timesheetConfig || [];

  const allDaysInWeek = [...Array(7)].map((_, index) => index + 1);
  const workDays = selectedWorkDays && selectedWorkDays?.length > 0 ? selectedWorkDays : allDaysInWeek;

  const columns = useMemo(
    () => (getDaysOfWeek() || []).filter(({ momentDate }) => workDays.includes(momentDate.day() + 1)),
    [workDays]
  );

  const { holidaysByDate, maxHolidaysPerWeek } = useGroupedHolidays();

  const onCellClick = useCallback(
    (day) => {
      const formattedDate = day?.format?.(API_DATE_FORMAT);
      if (disableAddEntryPoint) {
        return null;
      }

      if (isOrderOnlyCell) {
        return setShowNewOrder({ date: formattedDate });
      }

      if (!preventCellClick) {
        return setSelectedEvent({ date: formattedDate });
      }

      return null;
    },
    [disableAddEntryPoint, isOrderOnlyCell, preventCellClick, setSelectedEvent, setShowNewOrder]
  );

  const { projectsByDate, weeklyPlannerProjects, mainProjectsByDate } = useContext(SchedulingContextSelector);
  const { deleteProject, toggleArchiveProject } = useContext(SchedulingContextDispatcher);

  const filteredMainProjectsByDate = useMemo(
    () => filterMainProjectsByDateForMainEventSelection(mainProjectsByDate, projectFiltersForAccount),
    [mainProjectsByDate, projectFiltersForAccount]
  );

  const onWeeklyPlannerClick = useCallback(
    ({ weekStartDate, weekEndDate }) => {
      const formattedStartDate = moment(weekStartDate).format(API_DATE_FORMAT);
      const formattedEndDate = moment(weekEndDate).format(API_DATE_FORMAT);

      const url = `/${account?.id}/scheduling?key=weekly&startDate=${formattedStartDate}&endDate=${formattedEndDate}&groupBy=employee&date=weekly&branchId=${selectedBranchId}&isArchived=0&listView=0`;
      window.open(url, '_blank');
    },
    [account?.id, selectedBranchId]
  );

  const data = useMemo(() => {
    const week = [];
    const startDate = moment(start).startOf('week');

    const daysInWeek = 7;

    week.push(
      ...[...Array(daysInWeek)]
        .map(() => {
          const current = moment(startDate);
          startDate.add(1, 'day');
          return current;
        })
        // disaply in UI only the relevent working days
        // in workDay array: Sunday is 1, in moment.day() Sunday is 0
        .filter((date) => workDays.includes(date.day() + 1))
    );

    return week;
  }, [start, workDays]);

  const weeklyPlannersForCurrentWeek = useMemo(() => {
    if (!weeklyPlannerProjects?.length || !data?.length) return [];

    const weekDates = new Set(data.filter(Boolean).map((day) => day.format(API_DATE_FORMAT)));
    const weekPlanners = new Map();

    weeklyPlannerProjects.forEach((project) => {
      const projectDates = Object.keys(project.dates || {});
      const hasDateInWeek = projectDates.some((date) => weekDates.has(date));
      if (hasDateInWeek && !weekPlanners.has(project.id)) {
        weekPlanners.set(project.id, project);
      }
    });

    return Array.from(weekPlanners.values());
  }, [weeklyPlannerProjects, data]);

  // Extract main projects for current week
  const mainProjectsForCurrentWeek = useMemo(() => {
    if (!data?.length) return [];

    const weekMainProjects = new Map();
    data.filter(Boolean).forEach((day) => {
      const dateStr = day.format(API_DATE_FORMAT);
      const dayMainProjects = filteredMainProjectsByDate?.[dateStr] || [];
      dayMainProjects.forEach((project) => {
        if (!weekMainProjects.has(project.id)) {
          weekMainProjects.set(project.id, project);
        }
      });
    });

    return Array.from(weekMainProjects.values());
  }, [data, filteredMainProjectsByDate]);

  const mainProjectTrackCount = useMemo(() => getMainProjectBarTrackCount(mainProjectsForCurrentWeek, data), [
    data,
    mainProjectsForCurrentWeek
  ]);

  const SORT_BY_START_TIME = SORT_BY_TIME('startTime');

  const sortedProjects = useMemo(() => {
    const result = {};
    data.forEach((day) => {
      const formattedDate = day?.format?.(API_DATE_FORMAT);

      result[day] = showOnlyMainEvents
        ? []
        : projectsByDate[formattedDate]
            ?.filter((project) => !project.isWeeklyPlanner)
            ?.sort(({ dates: aDates }, { dates: bDates }) => {
              const { minTime: minTimeA } = aDates[formattedDate] || {};
              const { minTime: minTimeB } = bDates[formattedDate] || {};

              return SORT_BY_START_TIME({ startTime: minTimeA }, { startTime: minTimeB });
            });
    });
    return result;
  }, [SORT_BY_START_TIME, data, projectsByDate, showOnlyMainEvents]);

  return (
    <Wrapper isLoading={isLoading}>
      {isLoading && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}

      <Container>
        <Content>
          <Row isHeader>
            {columns.map(({ day }, index) => (
              <Cell key={index} isHeader>
                <HeaderCell>
                  <Trans>{day}</Trans>
                </HeaderCell>
              </Cell>
            ))}
          </Row>

          {(() => {
            // Vertical order: weekly planner strip → holiday chips (in cell flow) → main project strip
            const weekInYearIndex = data[0] ? moment(data[0]).format('YYYY-[W]WW') : null;
            const maxHolidaysInWeek = weekInYearIndex ? maxHolidaysPerWeek?.[weekInYearIndex] || 0 : 0;
            // Base top: 51px (day header), plus 30px chip + 4px margin-bottom per holiday
            const plannerTopOffset = 51 + maxHolidaysInWeek * (HOLIDAY_CHIP_HEIGHT + CHIPS_GAP);

            const weeklyPlannerCount =
              hideWeeklyPlanners || showOnlyMainEvents ? 0 : weeklyPlannersForCurrentWeek.length || 0;
            // Each planner is 28px height + 4px gap, plus 4px top margin
            const plannerRowHeight =
              weeklyPlannerCount > 0 ? CHIPS_GAP + weeklyPlannerCount * (PLANNER_BAR_HEIGHT + CHIPS_GAP) : 0;

            const mainProjectTopOffset = plannerTopOffset + (plannerRowHeight ? plannerRowHeight - CHIPS_GAP : 0);
            const mainProjectRowHeight =
              mainProjectTrackCount > 0 ? mainProjectTrackCount * (MAIN_PROJECT_BAR_HEIGHT + CHIPS_GAP) : 0;

            return (
              <WeekRowWrapper>
                {!hideWeeklyPlanners && !showOnlyMainEvents && weeklyPlannersForCurrentWeek.length > 0 && (
                  <BarRow $topOffset={plannerTopOffset}>
                    <WeeklyPlannerBar
                      weeklyPlannerProjects={weeklyPlannersForCurrentWeek}
                      onClick={onWeeklyPlannerClick}
                      weekDays={data}
                    />
                  </BarRow>
                )}
                {mainProjectTrackCount > 0 && (
                  <BarRow $topOffset={mainProjectTopOffset}>
                    <MainProjectBar
                      mainProjects={mainProjectsForCurrentWeek}
                      weekDays={data}
                      onClick={setSelectedMainProjectView}
                    />
                  </BarRow>
                )}

                <Row>
                  {data.map((day, columnIndex) => {
                    const isToday = day && moment().isSame(day, 'date');
                    const allowOnClick = !!day && !preventCellClick;
                    const dayWeekInYearIndex = moment(day).format('YYYY-[W]WW');

                    return (
                      <Cell
                        key={columnIndex}
                        isToday={isToday}
                        onClick={allowOnClick ? () => onCellClick(day) : undefined}
                        $dataCell>
                        <DayRenderer
                          weeklyPlannerRowHeight={plannerRowHeight}
                          mainProjectRowHeight={mainProjectRowHeight}
                          account={account}
                          push={push}
                          currentSpecialDays={holidaysByDate?.[moment(day).format('YYYY-MM-DD')]}
                          selectedBranchId={selectedBranchId}
                          setShowNewOrder={setShowNewOrder}
                          maxHolidaysInCurrentWeek={maxHolidaysPerWeek?.[dayWeekInYearIndex]}
                          setSelectedEvent={setSelectedEvent}
                          setSelectedBooking={setSelectedBooking}
                          setSelectedShift={setSelectedShift}
                          setPublishProject={setPublishProject}
                          setCopyEvent={setCopyEvent}
                          setAddQuickProject={setAddQuickProject}
                          mappedLabels={mappedLabels}
                          labels={labels}
                          mappedBranches={mappedBranches}
                          mappedLocations={mappedLocations}
                          mappedSuppliers={mappedSuppliers}
                          mappedTags={mappedTags}
                          mappedClientBranches={mappedClientBranches}
                          date={day}
                          formattedDate={day?.format?.(API_DATE_FORMAT)}
                          columnIndex={columnIndex}
                          sortedProjects={sortedProjects[day]}
                          deleteProject={deleteProject}
                          toggleArchiveProject={toggleArchiveProject}
                          isToday={isToday}
                        />
                      </Cell>
                    );
                  })}
                </Row>
              </WeekRowWrapper>
            );
          })()}
        </Content>
      </Container>
    </Wrapper>
  );
};

export default WeekLightView;
